import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCalendar, FiBook, FiInfo, FiMonitor } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export default function StudentCourseView() {
    const { classId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const loadCourseData = async () => {
            if (!currentUser) return;
            try {
                const data = await fetchAPI(`/student/get_class_details.php?class_id=${classId}&user_id=${currentUser.id}`);
                setCourseData(data);
            } catch (err) {
                console.error("Failed to load course details", err);
            } finally {
                setLoading(false);
            }
        };
        loadCourseData();
    }, [classId, currentUser]);

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading course details...</div>;

    if (!courseData) {
        return (
            <div className="p-12 text-center bg-white rounded-3xl border border-gray-100 shadow-sm max-w-2xl mx-auto mt-12">
                <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <FiInfo size={40} />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-4">Class Not Found</h2>
                <p className="text-gray-500 mb-8 font-medium">
                    We couldn't find the class you're looking for (ID: {classId}).
                    This might be due to a stale link or an old database record.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-8 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors uppercase text-sm"
                    >
                        Go Back
                    </button>
                    <button
                        onClick={() => navigate('/dashboard/courses')}
                        className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-100 uppercase text-sm"
                    >
                        Browse Teachers
                    </button>
                </div>
            </div>
        );
    }

    const { months } = courseData;

    return (
        <div className="space-y-6">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 rounded-3xl p-8 text-white relative overflow-hidden">
                <div className="relative z-10">
                    <h1 className="text-3xl font-bold mb-4">{courseData.name}</h1>
                    <div className="flex flex-wrap gap-6 text-sm opacity-90 font-medium">
                        <span className="flex items-center gap-2">• Grade: {courseData.grade}</span>
                        <span className="flex items-center gap-2">• Fee: LKR {courseData.fee}</span>
                        <span className="flex items-center gap-2">• Type: {courseData.type}</span>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">

                {/* Left: Months Grid */}
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6 border-b border-gray-200">
                        <button className="px-4 py-2 border-b-2 border-blue-600 text-blue-600 font-bold text-sm">2025</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {months.map((month) => (
                            <div key={month.id} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                                {month.payment_status === 'Pending' && (
                                    <div className="absolute top-0 right-0 bg-yellow-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-bl-lg z-10 animate-pulse">
                                        Pending Approval
                                    </div>
                                )}
                                {month.payment_status === 'Approved' && (
                                    <div className="absolute top-0 right-0 bg-green-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-bl-lg z-10">
                                        Unlocked
                                    </div>
                                )}

                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold text-gray-900">{month.name}</h3>
                                        <div className="flex items-center gap-2 mt-2">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${month.color} bg-opacity-20`}>
                                                <FiBook size={14} />
                                            </div>
                                            <span className="text-xs text-gray-500 font-medium">{month.lessons} Lessons</span>
                                        </div>
                                    </div>
                                    <FiInfo className="text-gray-300" />
                                </div>

                                <div className="grid grid-cols-2 gap-2 mt-4">
                                    <button
                                        disabled={month.payment_status !== 'Approved'}
                                        onClick={() => navigate(`/dashboard/courses/class/${classId}/lesson/${month.id}`)}
                                        className={`py-2 text-white text-xs font-bold rounded transition-colors ${month.payment_status === 'Approved' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed uppercase'}`}
                                    >
                                        {month.payment_status === 'Approved' ? 'VIEW' : 'LOCKED'}
                                    </button>
                                    <button
                                        onClick={() => navigate('/dashboard/payments', { state: { classId: classId, monthName: month.name } })}
                                        disabled={month.payment_status === 'Approved' || month.payment_status === 'Pending'}
                                        className={`py-2 text-xs font-bold rounded transition-colors ${month.payment_status === 'Approved' || month.payment_status === 'Pending' ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                                    >
                                        GET MONTH
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Payment Sidebar - Hidden on Mobile */}
                <div className="hidden lg:block w-full lg:w-80 space-y-6">
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm sticky top-6">
                        <div className="bg-gray-50 w-full aspect-video rounded-xl mb-6 flex items-center justify-center text-gray-300">
                            <FiMonitor size={48} />
                        </div>

                        <div className="bg-red-50 text-red-600 text-xs font-bold py-1 px-2 rounded mb-4 inline-block">

                        </div>

                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <FiCalendar className="text-gray-400" />
                                <span>Start Date: <span className="font-bold text-gray-900">March 19th 2025</span></span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <span className="font-bold text-gray-900">Fee: LKR 1500</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <span className="font-bold text-gray-900">Payment Scheme: Monthly</span>
                            </div>
                        </div>

                        <button className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 mb-3 hover:bg-blue-700 transition-colors">
                            GET CLASS
                        </button>
                        <button className="w-full py-3 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-100 transition-colors mb-6">
                            REGISTER
                        </button>

                        <div className="border-t border-gray-100 pt-4">
                            <h4 className="text-xs font-bold text-gray-400 uppercase mb-3">Teacher</h4>
                            <div className="flex items-center gap-3">
                                <img src="https://scontent.fcmb2-2.fna.fbcdn.net/v/t39.30808-6/525562404_122093493092968205_3300697237847986232_n.webp?_nc_cat=104&ccb=1-7&_nc_sid=6ee11a&_nc_eui2=AeFWDztpsegHZ5TC_5cxdi24rp1X2uaeFOiunVfa5p4U6LCf1ry2MCdjXyqtKbn-7O5gpN4Bcx0R8XEh9K_aYKya&_nc_ohc=67m1WeM7EgsQ7kNvwEeUPz6&_nc_oc=AdmoEvJpUO07I_QHT81_e5R1LPcV0RKGNb-K3DGzPthd_J2ZJsivdDW5q99GqvSObPg&_nc_zt=23&_nc_ht=scontent.fcmb2-2.fna&_nc_gid=s0EnWmhHenqGVa37KJyrvg&oh=00_AflWkgQOzFNnrhKUwTMsrHotf4dNOzZWN_EHxx2btNsxFg&oe=694FDE0B" alt="Teacher" className="w-10 h-10 rounded-full" />
                                <div>
                                    <p className="text-sm font-bold text-gray-900">Sudesh Rathnayake</p>
                                    <button className="text-xs text-blue-600 font-bold uppercase mt-1">View Teacher</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
